/**
 * A record's complete desired override state = persisted ⊕ staged (Sets applied,
 * Clears removed), for every kind. Both PUT /overrides (a REPLACE) and PUT
 * /preview (a dry run of that REPLACE — an empty or omitted kind shows as
 * cleared) take this state, so preview and confirm build it the same way.
 *
 * Value overrides aren't staged by the client (reward overrides replaced
 * metric-tiering) — persisted ones are echoed so the replace doesn't drop them.
 */
const buildOverrideState = ({
  persisted,
  persistedRewardOverrides,
  stagedCriteria,
  stagedQualifier,
  stagedRewardOverrides,
}: {
  // GET /overrides: criteria, qualifier and value overrides.
  persisted: BonusTypes.Overrides.Persisted | undefined;
  // Reward overrides live on the record (DriverRewardsOutput.rewardOverrides).
  persistedRewardOverrides:
    | { rewardName: string; rate?: number; amount?: number }[]
    | undefined;
  stagedCriteria: BonusTypes.Overrides.UserInputCriteria[] | undefined;
  stagedQualifier: BonusTypes.Overrides.UserInputQualifier | undefined;
  stagedRewardOverrides:
    | BonusTypes.Overrides.UserInputRewardOverride[]
    | undefined;
}): BonusTypes.Overrides.OverrideState => {
  const criteriaByField = new Map<string, boolean>();
  (persisted?.criteriaOverrides ?? []).forEach((o) => {
    criteriaByField.set(o.criterionField, o.value);
  });
  (stagedCriteria ?? []).forEach((o) => {
    if (o.action === 'Clear') {
      criteriaByField.delete(o.criterionField);
    } else {
      criteriaByField.set(o.criterionField, o.value);
    }
  });

  const qualifierOverride = (() => {
    if (stagedQualifier) {
      return stagedQualifier.action === 'Clear'
        ? null
        : { value: stagedQualifier.value };
    }
    return persisted?.qualifierOverride
      ? { value: persisted.qualifierOverride.value }
      : null;
  })();

  const rewardsByName = new Map<
    string,
    BonusTypes.Overrides.UnifiedRewardOverride
  >();
  (persistedRewardOverrides ?? []).forEach((o) => {
    rewardsByName.set(o.rewardName, {
      rewardName: o.rewardName,
      rate: o.rate,
      amount: o.amount,
    });
  });
  (stagedRewardOverrides ?? []).forEach((s) => {
    if (s.action === 'Clear') {
      rewardsByName.delete(s.rewardName);
    } else {
      rewardsByName.set(s.rewardName, {
        rewardName: s.rewardName,
        rate: s.rate,
        amount: s.amount,
      });
    }
  });

  return {
    criteriaOverrides: Array.from(criteriaByField.entries()).map(
      ([criterionField, value]) => ({ criterionField, value }),
    ),
    qualifierOverride,
    valueOverrides: (persisted?.valueOverrides ?? []).map((o) => ({
      field: o.field,
      value: o.value,
    })),
    rewardOverrides: Array.from(rewardsByName.values()),
  };
};

export const hasAnyOverride = (
  state: BonusTypes.Overrides.OverrideState,
): boolean =>
  state.criteriaOverrides.length > 0 ||
  !!state.qualifierOverride ||
  state.valueOverrides.length > 0 ||
  state.rewardOverrides.length > 0;

// The PUT /overrides body for a state (empty kinds omitted — the REPLACE clears
// them either way) plus the change's comment and reason codes.
export const toOverridesRequest = ({
  state,
  comment,
  reasonCodes,
}: {
  state: BonusTypes.Overrides.OverrideState;
  comment: string;
  reasonCodes: string[];
}): BonusTypes.Overrides.UnifiedOverridesRequest => ({
  criteriaOverrides:
    state.criteriaOverrides.length > 0 ? state.criteriaOverrides : undefined,
  qualifierOverride: state.qualifierOverride ?? undefined,
  valueOverrides:
    state.valueOverrides.length > 0 ? state.valueOverrides : undefined,
  rewardOverrides:
    state.rewardOverrides.length > 0 ? state.rewardOverrides : undefined,
  comment,
  reasonCodes: reasonCodes.length > 0 ? reasonCodes : undefined,
});

export default buildOverrideState;
